import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  type AnalyticsQuery,
  DashboardPicker,
  metricFormat,
  RangePicker,
  RepAnalyticsSkeleton,
  RepAnalyticsView,
  TeamAnalyticsView,
} from "@vd/ui";
import { useEffect } from "react";
import { z } from "zod";
import { analytics, costKeys } from "../../api/costs";
import { useDashboard, useDashboardList, useSetDefault } from "../../api/dashboards";
import { isAdmin, requireManager, useMe } from "../../auth/useMe";
import { CallsHealthCard } from "../../components/costs/CallsHealthCard";
import { ConsoleDashboard } from "../../components/dashboards/ConsoleDashboard";
import { REP_REVIEW_ANCHOR, RepPinnedReview, TeamPinnedReviews } from "../../components/reviews/PinnedReview";
import { RepReviewsPanel } from "../../components/reviews/RepReviewsPanel";
import { SpokenExamplesSwitch } from "../../components/reviews/SpokenExamplesSwitch";
import { ErrorState, PageHeader } from "../../components/ui";
import { useDashboardName, useRepAnalyticsParents } from "../../lib/crumbLabels";
import { useNavigate } from "../../lib/navigate";

// Rep analytics for admins and Rep Managers (DESIGN.md, "Rep analytics (console)" and
// "Analytics dashboards", Console): the dashboard picker in the title; Overview is the team,
// then any rep they manage, with that rep's performance reviews; any other dashboard is drawn
// from its spec, with sharing for the owner.

const OVERVIEW_ID = "builtin:overview";

const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const searchSchema = z.object({
  dashboard: z
    .string()
    .regex(/^[\w:-]{1,64}$/)
    .optional()
    .catch(undefined),
  range: z.enum(["today", "week", "month", "custom"]).optional().catch(undefined),
  from: day.optional().catch(undefined),
  to: day.optional().catch(undefined),
  view: z.enum(["me", "team", "owner"]).optional().catch(undefined),
  rep: z.string().optional().catch(undefined),
  rep_name: z.string().optional().catch(undefined),
  /** The campaign filter (2.15), e.g. from the Costs page's Campaigns tab. */
  campaign: z
    .string()
    .regex(/^[\w-]{1,64}$/)
    .optional()
    .catch(undefined),
  /** Cost tiles narrowed to one cost type ("Only Calls"). */
  cost_type: z
    .string()
    .regex(/^[a-z_]{1,40}$/)
    .optional()
    .catch(undefined),
});

export const Route = createFileRoute("/admin/rep-analytics")({
  staticData: {
    crumb: {
      label: "Rep analytics",
      // A rep opened on a dashboard is a level inside this page: "Samples / Jared Bailey", and
      // Back, Esc or the crumb return to the dashboard. The page as a level is the dashboard.
      useLabel: ({ search }) => {
        const dashboard = useDashboardName(search.dashboard);
        return search.rep ? String(search.rep_name ?? "Rep") : dashboard;
      },
      useParents: ({ search }) => useRepAnalyticsParents(search),
      levelKeys: ["rep"],
    },
  },
  validateSearch: (s) => searchSchema.parse(s),
  beforeLoad: requireManager,
  component: RepAnalyticsPage,
});

function RepAnalyticsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const set = (patch: Partial<z.infer<typeof searchSchema>>) =>
    navigate({
      search: (p) => ({ ...p, ...patch }),
      replace: !("rep" in patch) && !("dashboard" in patch),
    });
  const list = useDashboardList();
  const star = useSetDefault();
  // The open dashboard: the address, else the default once the list is in (Overview if it fails).
  const id = search.dashboard ?? (list.data ? list.data.default_id : list.isError ? OVERVIEW_ID : null);
  // Overview's cards are the Analytics screen's numbers, which take no campaign filter: drop it
  // from the address rather than show unfiltered numbers under a campaign.
  const overviewWithCampaign = id === OVERVIEW_ID && Boolean(search.campaign);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `set` is a fresh closure each render.
  useEffect(() => {
    if (overviewWithCampaign) void set({ campaign: undefined });
  }, [overviewWithCampaign]);
  // Without a range in the address, the dashboard's own `default_range` (Costs: this month).
  const openSpec = useDashboard(id && id !== OVERVIEW_ID ? id : null).data?.spec;
  const q: AnalyticsQuery = {
    range: search.range ?? openSpec?.default_range ?? "week",
    from: search.from,
    to: search.to,
  };
  const spec = id !== null && id !== OVERVIEW_ID;
  const team = useQuery({
    queryKey: costKeys.team(q),
    queryFn: () => analytics.team(q),
    enabled: !search.rep && id === OVERVIEW_ID,
    placeholderData: (p) => p,
  });
  const rep = useQuery({
    queryKey: costKeys.rep(search.rep ?? "", q),
    queryFn: () => analytics.rep(search.rep as string, q),
    enabled: !!search.rep && !spec,
  });
  const active = search.rep ? rep : team;
  const data = active.data;
  // Admins see Calls health on Costs; Rep Managers see it here (ADR 0023).
  const admin = isAdmin(useMe());
  const all = list.data ? [...list.data.builtins, ...list.data.mine, ...list.data.shared] : [];
  const open = all.find((d) => d.id === id);
  const name = open?.name ?? (id === OVERVIEW_ID ? "Overview" : "");
  // A rep opens on the same dashboard when the viewer may narrow it (their own or a built-in);
  // from someone else's, on Overview.
  const openRep = (rep: string, repName: string) =>
    open && (open.mine || open.builtin)
      ? set({ rep, rep_name: repName })
      : set({ dashboard: OVERVIEW_ID, rep, rep_name: repName });
  const rangePicker = (
    <RangePicker value={q} onChange={(v) => set({ range: v.range, from: v.from, to: v.to })} />
  );

  const title = search.rep ? null : (
    <div className="flex min-w-0 items-center gap-1 pb-4">
      <h1 className="m-0 font-display font-medium text-[28px] text-ink-3 leading-8">Rep analytics</h1>
      <span aria-hidden className="px-1.5 font-display text-[28px] text-ink-4 leading-8">
        /
      </span>
      <DashboardPicker
        size="lg"
        list={list.data ?? { builtins: [], mine: [], shared: [], default_id: OVERVIEW_ID }}
        value={id ?? ""}
        defaultId={list.data?.default_id ?? OVERVIEW_ID}
        name={name}
        onOpen={(d) => set({ dashboard: d, view: undefined, cost_type: undefined })}
        onStar={(d) => star.mutate(d)}
      />
    </div>
  );

  // Nothing is fetched for a dashboard until the default is known: no Overview flash.
  if (id === null && !search.rep) {
    return (
      <>
        {title}
        <RepAnalyticsSkeleton />
      </>
    );
  }

  if (spec && id && search.rep) {
    const repName = search.rep_name ?? "Rep";
    return (
      <>
        <PageHeader title={repName} />
        <ConsoleDashboard
          key={`${id}-${search.rep}`}
          id={id}
          range={q.range}
          from={q.from}
          to={q.to}
          view={undefined}
          rep={search.rep}
          onView={() => {}}
          rangePicker={rangePicker}
          reviewSlot={<RepPinnedReview key={`pin-${search.rep}`} userId={search.rep} name={repName} />}
        />
        <div id={REP_REVIEW_ANCHOR} className="mt-4 scroll-mt-4">
          <RepReviewsPanel key={search.rep} userId={search.rep} name={repName} />
        </div>
      </>
    );
  }

  if (spec && id) {
    return (
      <>
        {title}
        <ConsoleDashboard
          key={id}
          id={id}
          range={q.range}
          from={q.from}
          to={q.to}
          view={search.view}
          onView={(v) => set({ view: v })}
          campaign={search.campaign}
          costType={search.cost_type}
          onCampaign={(c) => set({ campaign: c })}
          onCostType={(t) => set({ cost_type: t })}
          rangePicker={rangePicker}
          onOpenRep={openRep}
          reviewSlot={<TeamPinnedReviews onOpenRep={(r) => openRep(r.rep_user_id, r.rep_name)} />}
        />
      </>
    );
  }

  return (
    <>
      {search.rep ? (
        <PageHeader title={search.rep_name ?? rep.data?.name ?? "Rep"}>
          {data ? metricFormat.compareLine(data.range, data.period, data.previous_period) : null}
        </PageHeader>
      ) : (
        <>
          {title}
          <p className="-mt-2 mb-3 text-caption text-ink-3">
            {data ? metricFormat.compareLine(data.range, data.period, data.previous_period) : ""}
          </p>
        </>
      )}
      <RangePicker
        value={q}
        onChange={(v) => set({ range: v.range, from: v.from, to: v.to })}
        className="mb-4"
      />
      {active.isError ? (
        <ErrorState error={active.error} onRetry={() => active.refetch()} />
      ) : search.rep && rep.data ? (
        <>
          <RepPinnedReview key={`pin-${search.rep}`} userId={search.rep} name={rep.data.name} />
          <div id={REP_REVIEW_ANCHOR} className="scroll-mt-4">
            <RepReviewsPanel key={search.rep} userId={search.rep} name={rep.data.name} />
          </div>
          <RepAnalyticsView
            data={rep.data}
            subject={rep.data.name}
            onOpenLink={(url) => window.open(url, "_blank", "noopener")}
          />
        </>
      ) : !search.rep && team.data ? (
        <>
          <TeamPinnedReviews onOpenRep={(r) => set({ rep: r.rep_user_id, rep_name: r.rep_name })} />
          {admin ? null : (
            <div className="mb-4 flex flex-col gap-3">
              <div className="flex justify-end">
                <SpokenExamplesSwitch />
              </div>
              <CallsHealthCard />
            </div>
          )}
          <TeamAnalyticsView data={team.data} onOpenRep={(id, name) => set({ rep: id, rep_name: name })} />
        </>
      ) : (
        <RepAnalyticsSkeleton />
      )}
    </>
  );
}
